import { ReactElement, useMemo, useState } from "react";

import { ClientResponseError } from "@/Common/Api/ClientResponse";
import { Guid } from "@/Common/Models/Guid";
import { FlowsheetCareComponentValidatorHelper } from "@/Flowsheets/Helpers/FlowsheetCareComponentValidatorHelper";
import { useDeleteFlowsheet } from "@/Flowsheets/Hooks/useDeleteFlowsheet";
import useEmployeeContext from "@/Flowsheets/Hooks/useEmployeeContext";
import { useSaveFlowsheet } from "@/Flowsheets/Hooks/useSaveFlowsheet";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { Flowsheet as FlowsheetModel } from "@/Flowsheets/Models/Flowsheet";
import { FlowsheetFilterSettings } from "@/Flowsheets/Models/FlowsheetFilterSettings";
import { DeleteFlowsheetModal } from "../DeleteFlowsheetModal/DeleteFlowsheetModal";
import { FlowsheetFilterSettingsPopup } from "../FlowsheetFilterSettingsPopup/FlowsheetFilterSettingsPopup";
import { FlowsheetWarningPopup } from "../FlowsheetWarningPopup/FlowsheetWarningPopup";
import { LeaveFlowsheetConfirmationModal } from "../LeaveFlowsheetConfirmationModal/LeaveFlowsheetConfirmationModal";
import { FlowsheetContent } from "./FlowsheetContent";
import { FlowsheetFooter } from "./FlowsheetFooter";
import { FlowsheetHeader } from "./FlowsheetHeader";

import styles from "./Flowsheet.module.scss";

interface IFlowsheetProps {
    readonly flowsheet?: FlowsheetModel;
    title: string;
    encounterDate: Date;
    leaveFlowsheet: () => void;

    /**
     * The care component data of the flowsheet
     */
    careComponentData: CareComponent[];

    /**
     * The function to call when the care component data is updated
     */
    setCareComponentData: (careComponentData: CareComponent[]) => void;

    /**
     * The function to call when the user clicks on the configure flowsheet button
     */
    onClickConfigureFlowsheet: () => void;

    /**
     * The ContactId of the patient the flowsheet belongs to
     */
    patientContactId: Guid;

    /**
     * The filter settings applied to each Care Component.
     * Its default value needs to be set based on the flowsheet configuration.
     */
    filterSettings: FlowsheetFilterSettings;

    /**
     * The function to update the filter settings
     */
    setFilterSettings: (filterSettings: FlowsheetFilterSettings) => void;
}
function applyConfirmedFilterSettings(
    updatedSettings: FlowsheetFilterSettings,
    isMainFilter: boolean
): FlowsheetFilterSettings {
    if (isMainFilter)
        return updatedSettings;

    const updatedSubFilters = updatedSettings.CareComponentFilterSettings?.map(careComponentfilterSettings => ({
        ...careComponentfilterSettings,
        OutsideTreatmentFrequency: updatedSettings.OutsideTreatmentFrequency,
        OutsideTreatmentTarget: updatedSettings.OutsideTreatmentTarget,
        ResultsDurationType: updatedSettings.ResultsDurationType,
        StartDate: updatedSettings.StartDate,
        EndDate: updatedSettings.EndDate
    }));

    return new FlowsheetFilterSettings({
        ...updatedSettings,
        CareComponentFilterSettings: updatedSubFilters
    });
}

function Flowsheet({ careComponentData, flowsheet, title, encounterDate, patientContactId, filterSettings, leaveFlowsheet, setCareComponentData, onClickConfigureFlowsheet, setFilterSettings }: Readonly<IFlowsheetProps>): ReactElement {
    const [isLeaveFlowsheetConfirmationModalOpen, setIsLeaveFlowsheetConfirmationModalOpen] = useState(false);
    const [isDeleteFlowsheetModalOpen, setIsDeleteFlowsheetModalOpen] = useState(false);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [fieldValidityMap, setFieldValidityMap] = useState<Record<string, boolean>>(() =>
        FlowsheetCareComponentValidatorHelper.getInitialFieldValidityMap(careComponentData)
    );
    const [showSettingsModal, setShowSettingsModal] = useState(false);

    const isAnyFieldInvalid = Object.values(fieldValidityMap).some(isValid => isValid === false);

    const handleFieldValidityChange = (id: string, isValid: boolean): void => {
        setFieldValidityMap(prev => ({
            ...prev,
            [id]: isValid
        }));
    };

    const handleFilterSelected = (filterName: string): void => {
        if (!filterSettings?.CareComponentFilterSettings)
            return;
        const updatedSettings = {
            ...filterSettings,
            CareComponentFilterSettings: filterSettings.CareComponentFilterSettings.map(setting => ({
                ...setting,
                IsEnabled: setting.Name === filterName
            }))
        };

        setFilterSettings(updatedSettings);
        setShowSettingsModal(true);
    };

    function displayError(error: ClientResponseError): void {
        setErrorMessages(error.errors.map(p => p.Message));
    }

    const {
        saveFlowsheet,
        reset: resetSaveFlowsheet
    } = useSaveFlowsheet({
        onSuccess: leaveFlowsheet,
        onError: displayError
    });

    const {
        deleteFlowsheet,
        reset: resetDeleteFlowsheet
    } = useDeleteFlowsheet({
        onSuccess: leaveFlowsheet,
        onError: displayError
    });

    function resetCommand(): void {
        resetDeleteFlowsheet();
        resetSaveFlowsheet();
    }

    const employeeContext = useEmployeeContext();

    async function handleCancel(): Promise<void> {
        if (flowsheet.isNotApproved())
            setIsLeaveFlowsheetConfirmationModalOpen(true);
        else
            leaveFlowsheet();
    }

    const isSaveButtonDisabled = useMemo(() => isAnyFieldInvalid || !FlowsheetCareComponentValidatorHelper.isFlowsheetValid(careComponentData), [careComponentData, isAnyFieldInvalid]);

    return (
        <>
            {showSettingsModal && (
                <FlowsheetFilterSettingsPopup
                    onConfirm={(updatedSettings, isMainFilter) => {
                        setFilterSettings(applyConfirmedFilterSettings(updatedSettings, isMainFilter));
                        setShowSettingsModal(false);
                    }}
                    onClose={() => setShowSettingsModal(false)}
                    encounterDate={encounterDate}
                    flowsheetFilterSettings={filterSettings}
                    flowsheetId={flowsheet.Id}
                />
            )}
            {flowsheet && (
                <div className={styles.flowsheetLayoutContainer}>
                    <FlowsheetHeader
                        name={title}
                        createdBy={employeeContext.getFullNameWithPrefixContactFacet(flowsheet.CreatedByEmployeeId)}
                        signedOffBy={flowsheet.ApprovedByEmployeeId ? employeeContext.getFullNameWithPrefixContactFacet(flowsheet.ApprovedByEmployeeId) : undefined}
                        signedOffDate={flowsheet.ApprovalDate}
                        onClickConfigureFlowsheet={onClickConfigureFlowsheet}
                        encounterDate={encounterDate}
                        flowsheetFilterSettings={filterSettings}
                        onFilterSelected={handleFilterSelected}
                        flowsheetId={flowsheet.Id}
                    />
                    <FlowsheetContent
                        isEditable={flowsheet.isNotApproved()}
                        careComponentData={careComponentData}
                        encounterDate={encounterDate}
                        encounterId={flowsheet.EncounterId}
                        filterSettings={filterSettings}
                        setCareComponentData={setCareComponentData}
                        onCareComponentValidityChange={handleFieldValidityChange}
                        onFilterSelected={handleFilterSelected}
                        patientContactId={patientContactId}
                    />
                    <FlowsheetFooter
                        onSaveClicked={() => {
                            const flowsheetToSave = new FlowsheetModel(flowsheet);
                            flowsheetToSave.CareComponents = careComponentData;
                            saveFlowsheet(flowsheetToSave);
                        }}
                        onCancelClicked={() => handleCancel()}
                        onDeleteClicked={() => setIsDeleteFlowsheetModalOpen(true)}
                        showDeleteButton={flowsheet.isNotApproved()}
                        showSaveButton={flowsheet.isNotApproved()}
                        disableSaveButton={isSaveButtonDisabled}
                    />
                    {isLeaveFlowsheetConfirmationModalOpen && (
                        <LeaveFlowsheetConfirmationModal
                            onConfirm={() => leaveFlowsheet()}
                            onCancel={() => setIsLeaveFlowsheetConfirmationModalOpen(false)}
                        />
                    )}
                    {isDeleteFlowsheetModalOpen && (
                        <DeleteFlowsheetModal
                            onDelete={() => {
                                setIsDeleteFlowsheetModalOpen(false);
                                deleteFlowsheet({ flowsheetId: flowsheet.Id, encounterId: flowsheet.EncounterId });
                            }}
                            onClose={() => setIsDeleteFlowsheetModalOpen(false)}
                        />
                    )}
                </div>
            )}
            {errorMessages?.any() && (
                <FlowsheetWarningPopup
                    errorMessages={errorMessages}
                    onConfirm={() => {
                        setErrorMessages(null);
                        resetCommand();
                    }}
                />
            )}
        </>
    );
}

export { applyConfirmedFilterSettings, Flowsheet };
